import Pubnub from 'pubnub';
import { getTrackingTokenFromLocalStorage } from 'helpers/browser';
import { PublicCallback } from 'globals/types';
import { getStore } from 'core_main/store';

export class PubNubInstance {
  instance: Pubnub | undefined;

  /**
   * @param customerIdOverride When re-keying, the store may still hold the
   * pre-merge customer; pass the target explicitly so uuid matches the
   * channels we are about to subscribe to.
   */
  constructor(customerIdOverride?: string) {
    const store = getStore();

    const { publishKey, subscribeKey } = store.settings.sharedPubNubKeySet;
    const customerId = customerIdOverride || store.customerId;
    const trackingToken = getTrackingTokenFromLocalStorage();

    if (trackingToken !== null && customerId) {
      this.instance = new Pubnub({
        uuid: customerId,
        authKey: trackingToken,
        subscribeKey,
        publishKey,
        restore: true,
        ssl: true,
      });
    }
  }

  listChannels = (
    listChannelsParameters: Pubnub.ListChannelsParameters,
    cb?: PublicCallback,
  ) => {
    if (!this.instance) return undefined;

    return this.instance?.channelGroups.listChannels(
      listChannelsParameters,
      (status, response) => {
        const { error } = status;
        if (cb && error === false) {
          cb(response);
        }
      },
    );
  };

  fetchMessages = (
    fetchMessagesParameters: Pubnub.FetchMessagesParameters,
    cb?: PublicCallback,
  ) => {
    if (!this.instance) return undefined;

    return this.instance?.fetchMessages(
      fetchMessagesParameters,
      (status, response) => {
        const { error } = status;
        if (cb && error === false) {
          cb(response);
        }
      },
    );
  };

  messageCounts = (
    messageCountsParameters: Pubnub.MessageCountsParameters,
    cb?: PublicCallback,
  ) => {
    if (!this.instance) return undefined;

    return this.instance?.messageCounts(
      messageCountsParameters,
      (status, response) => {
        const { error } = status;
        if (cb && error === false) {
          cb(response);
        }
      },
    );
  };
}

let PubNubMetaInstance: PubNubInstance | undefined;
let PubNubConversationInstance: PubNubInstance | undefined;
/** Bumped on every clear/stop so stale listeners cannot mutate the new client. */
let pubNubClientGeneration = 0;
let metaListener: Pubnub.ListenerParameters | undefined;
let conversationListener: Pubnub.ListenerParameters | undefined;

export const getPubNubClientGeneration = (): number => pubNubClientGeneration;

export const getMetaPubNub = (
  customerIdOverride?: string,
): PubNubInstance | undefined => {
  if (getTrackingTokenFromLocalStorage() === null) return undefined;
  if (!PubNubMetaInstance) {
    PubNubMetaInstance = new PubNubInstance(customerIdOverride);
  }
  return PubNubMetaInstance;
};

export const getConversationPubNub = (
  customerIdOverride?: string,
): PubNubInstance | undefined => {
  if (getTrackingTokenFromLocalStorage() === null) return undefined;
  if (!PubNubConversationInstance) {
    PubNubConversationInstance = new PubNubInstance(customerIdOverride);
  }
  return PubNubConversationInstance;
};

/**
 * Attach a meta listener, replacing any previous one so clearPubNub can
 * removeListener before dropping the client.
 */
export const addMetaPubNubListener = (
  listener: Pubnub.ListenerParameters,
): void => {
  const instance = PubNubMetaInstance?.instance;
  if (!instance) return;
  if (metaListener) {
    instance.removeListener(metaListener);
  }
  instance.addListener(listener);
  metaListener = listener;
};

/**
 * Attach a conversation listener, replacing any previous one so clearPubNub
 * can removeListener before dropping the client.
 */
export const addConversationPubNubListener = (
  listener: Pubnub.ListenerParameters,
): void => {
  const instance = PubNubConversationInstance?.instance;
  if (!instance) return;
  if (conversationListener) {
    instance.removeListener(conversationListener);
  }
  instance.addListener(listener);
  conversationListener = listener;
};

export const clearPubNub = () => {
  pubNubClientGeneration += 1;
  if (PubNubMetaInstance) {
    if (metaListener) {
      PubNubMetaInstance.instance?.removeListener(metaListener);
      metaListener = undefined;
    }
    PubNubMetaInstance.instance?.unsubscribeAll();
    PubNubMetaInstance = undefined;
  }
  if (PubNubConversationInstance) {
    if (conversationListener) {
      PubNubConversationInstance.instance?.removeListener(conversationListener);
      conversationListener = undefined;
    }
    PubNubConversationInstance.instance?.unsubscribeAll();
    PubNubConversationInstance = undefined;
  }
};

export const stopPubNub = () => {
  pubNubClientGeneration += 1;
  if (PubNubMetaInstance) {
    if (metaListener) {
      PubNubMetaInstance.instance?.removeListener(metaListener);
      metaListener = undefined;
    }
    PubNubMetaInstance.instance?.stop();
    PubNubMetaInstance = undefined;
  }
  if (PubNubConversationInstance) {
    if (conversationListener) {
      PubNubConversationInstance.instance?.removeListener(conversationListener);
      conversationListener = undefined;
    }
    PubNubConversationInstance.instance?.stop();
    PubNubConversationInstance = undefined;
  }
};

export const disconnect = () => {
  // TODO remove the typecast to any when the @types/pubnub types are updated to a version that has disconnect()
  if (PubNubMetaInstance) {
    (PubNubMetaInstance.instance as any).disconnect();
  }
  if (PubNubConversationInstance) {
    (PubNubConversationInstance.instance as any).disconnect();
  }
};

export const reconnect = () => {
  if (PubNubMetaInstance) {
    PubNubMetaInstance.instance?.reconnect();
  }
  if (PubNubConversationInstance) {
    PubNubConversationInstance.instance?.reconnect();
  }
};
